import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AppHeader } from '@/components/AppHeader';
import { Activity, Moon, TrendingUp, Brain } from 'lucide-react';

interface GarminData {
  recovery?: {
    bodyBattery?: number;
    restingHeartRate?: number;
    hrv?: number;
  };
  sleep?: {
    totalSleepTime?: number;
    sleepScore?: number;
    deepSleep?: number;
    remSleep?: number;
    lightSleep?: number;
  };
  activity?: {
    steps?: number;
    calories?: number;
    intensityMinutes?: number;
    stressLevel?: number;
  };
}

export default function GarminDashboard() {
  const [activeView, setActiveView] = useState<'overview' | 'recovery' | 'sleep' | 'activity' | 'insights'>('overview');
  const [garminData, setGarminData] = useState<GarminData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    loadUser();
    checkConnection();
    loadGarminData();
  }, []);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const checkConnection = async () => {
    // For demo, assume connected
    setIsConnected(true);
    setLoading(false);
  };

  const loadGarminData = async () => {
    // Demo data
    setGarminData({
      recovery: {
        bodyBattery: 78,
        restingHeartRate: 56,
        hrv: 65,
      },
      sleep: {
        totalSleepTime: 452,
        sleepScore: 82,
        deepSleep: 98,
        remSleep: 115,
        lightSleep: 239,
      },
      activity: {
        steps: 12450,
        calories: 2340,
        intensityMinutes: 45,
        stressLevel: 32,
      },
    });
  };

  const handleConnect = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('garmin-oauth', {
        body: { action: 'connect' }
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error connecting:', error);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/garmin-login';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onSignOut={handleSignOut} />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">THERAPPI<sup className="text-[0.6em]">®</sup> × Garmin Integration</h1>
          <p className="text-muted-foreground">
            Demo environment showcasing Garmin Connect integration for partnership application
          </p>
        </div>

        {!isConnected ? (
          <Card className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Connect Your Garmin Account</h2>
            <p className="text-muted-foreground mb-6">
              Connect your Garmin account to see how THERAPPI<sup className="text-[0.6em]">®</sup> analyses your biometric data
              and correlates it with your mental wellbeing practices.
            </p>
            <Button onClick={handleConnect} size="lg">
              Connect Garmin
            </Button>
          </Card>
        ) : (
          <Tabs value={activeView} onValueChange={(v) => setActiveView(v as any)} className="space-y-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="recovery">
                <Activity className="w-4 h-4 mr-2" />
                Recovery
              </TabsTrigger>
              <TabsTrigger value="sleep">
                <Moon className="w-4 h-4 mr-2" />
                Sleep
              </TabsTrigger>
              <TabsTrigger value="activity">
                <TrendingUp className="w-4 h-4 mr-2" />
                Activity
              </TabsTrigger>
              <TabsTrigger value="insights">
                <Brain className="w-4 h-4 mr-2" />
                Insights
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4">Garmin Integration Highlights</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-2">Body Battery™</h4>
                    <p className="text-3xl font-bold">{garminData?.recovery?.bodyBattery || 0}</p>
                    <p className="text-sm text-muted-foreground">Current energy level</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-2">Sleep Score</h4>
                    <p className="text-3xl font-bold">{garminData?.sleep?.sleepScore || 0}</p>
                    <p className="text-sm text-muted-foreground">Last night's quality</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-2">Stress Level</h4>
                    <p className="text-3xl font-bold">{garminData?.activity?.stressLevel || 0}</p>
                    <p className="text-sm text-muted-foreground">Current state</p>
                  </div>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="recovery">
              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4">Recovery Metrics</h3>
                <p className="text-muted-foreground">Garmin recovery data visualisation will appear here</p>
              </Card>
            </TabsContent>

            <TabsContent value="sleep">
              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4">Sleep Analysis</h3>
                <p className="text-muted-foreground">Garmin sleep data visualisation will appear here</p>
              </Card>
            </TabsContent>

            <TabsContent value="activity">
              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4">Activity & Stress</h3>
                <p className="text-muted-foreground">Garmin activity data visualisation will appear here</p>
              </Card>
            </TabsContent>

            <TabsContent value="insights">
              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4">THERAPPI<sup className="text-[0.6em]">®</sup> × Garmin Correlations</h3>
                <p className="text-muted-foreground">AI-powered insights correlating Garmin data with THERAPPI<sup className="text-[0.6em]">®</sup> practices</p>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
